import React, { useCallback, useEffect, useRef } from 'react';
import { useUser } from '../auth/useAuth';
import { useApi } from '../api/client';
import { useUserPrefs } from '../hooks/useUserPrefs';
import {
  DEFAULT_SIMPLE_VIBE,
  readLocalRuleConfig,
  readLocalRuleConfigIfPresent,
  sanitizeRuleConfig,
  serializeRuleConfig,
  writeLocalRuleConfig,
  type PersistedRuleConfig,
} from '../rules/ruleConfigModel';
import { ruleBuilderService } from '../services/ruleBuilderService';
import { useRuleBuilderStore } from './ruleBuilderStore';

const SAVE_DEBOUNCE_MS = 400;

function hasRuleConfigContent(config: PersistedRuleConfig | undefined): config is PersistedRuleConfig {
  if (!config) return false;
  return Object.values(config.byGame).some(
    (gameConfig) =>
      Boolean(gameConfig) &&
      (gameConfig.rules.length > 0 || gameConfig.simpleVibe !== DEFAULT_SIMPLE_VIBE)
  );
}

export const RuleBuilderPersistenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { prefs } = useUserPrefs();
  const { user, isLoaded } = useUser();
  const api = useApi();
  const userRef = useRef(user);
  const saveTimer = useRef<number | null>(null);
  const bootstrapped = useRef(false);
  const hydratedRemoteForUserId = useRef<string | null>(null);
  const initialGameId = useRef(prefs.gameId);
  const lastPersistedSerialized = useRef('');
  const queuedPersistSerialized = useRef('');

  userRef.current = user;

  const persistConfig = useCallback((config: PersistedRuleConfig, options?: { forceRemote?: boolean }) => {
    const sane = sanitizeRuleConfig(config);
    const serialized = serializeRuleConfig(sane);
    const user = userRef.current;
    if (!options?.forceRemote && (serialized === lastPersistedSerialized.current || serialized === queuedPersistSerialized.current)) return;

    if (!user) {
      lastPersistedSerialized.current = serialized;
      queuedPersistSerialized.current = '';
      writeLocalRuleConfig(sane);
      return;
    }

    if (saveTimer.current !== null) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }

    queuedPersistSerialized.current = serialized;

    saveTimer.current = window.setTimeout(() => {
      api
        .put('/v1/me/rule-config', { ruleConfig: sane })
        .then(() => {
          lastPersistedSerialized.current = serialized;
          if (queuedPersistSerialized.current === serialized) queuedPersistSerialized.current = '';
          writeLocalRuleConfig(sane);
        })
        .catch(() => {
          if (queuedPersistSerialized.current === serialized) queuedPersistSerialized.current = '';
          /* D1 remains source of truth; do not mirror failed signed-in writes locally. */
        });
    }, SAVE_DEBOUNCE_MS);
  }, [api]);

  // Hydrate before children mount so RulesSimpleMode does not issue a stale default-vibe stats request.
  if (!bootstrapped.current) {
    bootstrapped.current = true;
    ruleBuilderService.hydrateRuleConfig(readLocalRuleConfig(), {
      activeGameId: initialGameId.current,
    });
    const current = ruleBuilderService.exportRuleConfig();
    lastPersistedSerialized.current = serializeRuleConfig(current);
    writeLocalRuleConfig(current);
  }

  useEffect(() => {
    return useRuleBuilderStore.subscribe(() => {
      const state = useRuleBuilderStore.getState();
      if (!state.hasHydrated) return;
      persistConfig(state.exportRuleConfig());
    });
  }, [persistConfig]);

  useEffect(() => {
    if (!bootstrapped.current) return;
    ruleBuilderService.setActiveGame(prefs.gameId);
  }, [prefs.gameId]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) {
      hydratedRemoteForUserId.current = null;
      return;
    }
    if (hydratedRemoteForUserId.current === user.id) return;
    hydratedRemoteForUserId.current = user.id;
    const configAtRequestStart = serializeRuleConfig(ruleBuilderService.exportRuleConfig());

    api
      .get<{ ruleConfig?: unknown }>('/v1/me/rule-config')
      .then((body) => {
        if (serializeRuleConfig(ruleBuilderService.exportRuleConfig()) !== configAtRequestStart) return;
        const local = readLocalRuleConfigIfPresent();
        const shouldSeedRemote = body.ruleConfig === undefined || body.ruleConfig === null;
        const seedLocal = hasRuleConfigContent(local) ? local : undefined;
        const next = shouldSeedRemote && seedLocal ? sanitizeRuleConfig(seedLocal) : sanitizeRuleConfig(body.ruleConfig);
        const nextSerialized = serializeRuleConfig(next);

        queuedPersistSerialized.current = '';
        if (!shouldSeedRemote || !seedLocal) {
          lastPersistedSerialized.current = nextSerialized;
        }
        ruleBuilderService.hydrateRuleConfig(next, { activeGameId: prefs.gameId });

        if (shouldSeedRemote && seedLocal) {
          persistConfig(next, { forceRemote: true });
        } else {
          writeLocalRuleConfig(next);
        }
      })
      .catch(() => {
        /* keep local config */
      });
  }, [api, isLoaded, persistConfig, prefs.gameId, user]);

  useEffect(() => {
    return () => {
      if (saveTimer.current !== null) {
        window.clearTimeout(saveTimer.current);
      }
    };
  }, []);

  return <>{children}</>;
};
